// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import clsx from "clsx"
import { useCycleLatestSnapshot } from "@/queries/queries.eventJournal"
import { parseCycleSnapshotPayload } from "../_snapshotPayload"
import { deriveReferenceBlock } from "../referenceSet/block"

/**
 * `ReferenceBlock` — the side "Reference" fiche beside the cycle curves,
 * per the binding Claude Design mockup (variant 1b): a narrow 214px
 * accent-tinted card — hero max score (`best of N` disclosed), med/min
 * row, cost/run, truncated exec/arch model rows revealed on hover, and a
 * "see the agent →" link to the reference agent inspection surface
 * (`/agents/:agentId`). The accent (`--reference` token) is shared with
 * the chart's reference level line so the two read as one object. Every
 * figure derives from the persisted `reference_set` record.
 *
 * `reference_set` absent (feature off / pre-feature snapshot) → renders
 * nothing, so the side panel lays out exactly as today.
 *
 * Dark-theme only. The `?` help bubble toggles on click and closes on
 * any outside click.
 */
export interface ReferenceBlockProps {
  cycleId?: string
}

const CARD_BG =
  "linear-gradient(180deg, color-mix(in srgb, var(--reference) 16%, transparent), color-mix(in srgb, var(--reference) 5%, transparent)), linear-gradient(180deg,rgba(9,32,38,.9),rgba(6,24,28,.82))"
const CARD_BORDER = "color-mix(in srgb, var(--reference) 50%, transparent)"
const CARD_SHADOW =
  "0 0 26px color-mix(in srgb, var(--reference) 13%, transparent), inset 0 1px 0 rgba(255,255,255,0.03)"

export default function ReferenceBlock({ cycleId }: ReferenceBlockProps = {}) {
  const params = useParams<{ cycleId: string }>()
  const resolvedCycleId = cycleId ?? params.cycleId ?? ""

  const snapshotQuery = useCycleLatestSnapshot(
    resolvedCycleId.length > 0 ? resolvedCycleId : undefined,
  )

  const [popoverOpen, setPopoverOpen] = useState(false)

  useEffect(() => {
    if (!popoverOpen) return
    const close = () => setPopoverOpen(false)
    document.addEventListener("click", close)
    return () => document.removeEventListener("click", close)
  }, [popoverOpen])

  if (!snapshotQuery.isSuccess || snapshotQuery.data == null) return null

  const dto = parseCycleSnapshotPayload<CycleSnapshotDto>(
    snapshotQuery.data.payload_json,
  )
  if (dto === null) return null

  const block = deriveReferenceBlock(dto)
  if (block === null) return null

  const { isSingle } = block

  return (
    <section
      data-testid="reference-block"
      aria-label="Reference"
      className="relative box-border flex w-[214px] flex-none flex-col justify-between gap-3.5 rounded-[14px] border px-[18px] py-4"
      style={{
        borderColor: CARD_BORDER,
        background: CARD_BG,
        boxShadow: CARD_SHADOW,
      }}
    >
      <div className="flex items-center justify-between">
        <h3 className="m-0 text-[14px] font-semibold leading-none tracking-[-0.01em] text-foreground">
          Reference
        </h3>
        <button
          type="button"
          aria-label="What is the reference?"
          aria-expanded={popoverOpen}
          onClick={(e) => {
            e.stopPropagation()
            setPopoverOpen((open) => !open)
          }}
          className={clsx(
            "flex size-[18px] flex-none items-center justify-center rounded-full border font-mono text-[10px] font-semibold transition-colors duration-150",
            popoverOpen
              ? "border-primary text-primary"
              : "border-text-tertiary text-text-tertiary hover:border-primary hover:text-primary",
          )}
          style={
            popoverOpen
              ? { boxShadow: "0 0 8px rgba(69,240,200,0.3)" }
              : undefined
          }
        >
          ?
        </button>
      </div>

      {popoverOpen && (
        <div
          data-testid="reference-help-popover"
          role="tooltip"
          className="absolute left-3 top-[42px] z-20 w-[262px] rounded-[11px] px-[13px] py-3"
          style={{
            background: "#061a1f",
            border: "1px solid color-mix(in oklab, var(--mint) 35%, transparent)",
            boxShadow:
              "0 12px 34px rgba(0,0,0,.55), 0 0 18px rgba(69,240,200,.12)",
          }}
        >
          <p className="mb-2 text-[11px] leading-[1.4] text-text-secondary">
            <strong className="font-semibold text-foreground">
              The reference is your &quot;without Ebiose&quot; comparison point.
            </strong>
          </p>
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {[
              "This is it: the best single-prompt agent — one node, no branching.",
              "Same brief, same evaluation dataset as the population — a fair comparison.",
              "Scored once, then frozen — it never evolves and never joins the pool.",
            ].map((line) => (
              <li
                key={line}
                className="mb-0 ml-0 list-none pl-0 font-mono text-[10.5px] leading-[1.35] text-text-tertiary"
              >
                {line}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-[3px]">
          <span
            className="font-mono text-[14px] font-medium uppercase tracking-[0.08em]"
            style={{ color: "var(--reference)" }}
          >
            {isSingle ? "score · single agent" : `max · best of ${block.n}`}
          </span>
          <span className="text-[63px] font-semibold leading-[0.85] tracking-[-0.025em] text-foreground">
            {block.maxLabel}
          </span>
        </div>
        {!isSingle && block.medianLabel !== null && block.minLabel !== null && (
          <div className="flex gap-3.5 font-mono text-[16.5px] font-medium text-text-tertiary">
            <span>
              med{" "}
              <b className="font-semibold text-text-secondary">
                {block.medianLabel}
              </b>
            </span>
            <span>
              min{" "}
              <b className="font-semibold text-text-secondary">
                {block.minLabel}
              </b>
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-[7px]">
        <div className="flex items-center justify-between gap-2.5">
          <span className="font-mono text-[9.5px] font-medium uppercase tracking-[0.06em] text-text-tertiary">
            Cost / run
          </span>
          <span className="font-mono text-[12px] font-medium text-foreground">
            {block.costPerRunLabel}
          </span>
        </div>
        <ModelRow abbr="exec" role="execution" model={block.executionModel} />
        <ModelRow abbr="arch" role="architect" model={block.architectModel} />
      </div>

      {block.agentUuid !== null ? (
        <Link
          to={`/agents/${block.agentUuid}`}
          className="inline-flex items-center gap-[5px] text-[11.5px] font-semibold text-primary transition-colors hover:text-done-fg"
        >
          see the agent →
        </Link>
      ) : (
        <span className="inline-flex items-center gap-[5px] text-[11.5px] font-semibold text-text-muted">
          see the agent →
        </span>
      )}
    </section>
  )
}

/**
 * One `exec` / `arch` row — the model id truncates to the card's 214px
 * and the full `role · model` reveals in a hover tooltip (mockup 1b).
 */
const ModelRow = ({
  abbr,
  role,
  model,
}: {
  abbr: string
  role: string
  model: string
}) => (
  <div className="group relative flex items-center justify-between gap-2.5">
    <span className="flex-none font-mono text-[9.5px] font-medium uppercase tracking-[0.06em] text-text-tertiary">
      {abbr}
    </span>
    <span className="max-w-[62%] truncate text-right font-mono text-[12px] font-medium text-foreground group-hover:text-primary">
      {model}
    </span>
    <span
      role="tooltip"
      className="pointer-events-none absolute bottom-full left-0 z-10 mb-[7px] whitespace-nowrap rounded-[7px] border border-border-strong px-[9px] py-1.5 font-mono text-[11px] font-medium text-foreground opacity-0 transition-opacity duration-150 group-hover:opacity-100"
      style={{
        background: "#04141a",
        boxShadow: "0 8px 24px rgba(0,0,0,.5)",
      }}
    >
      {role} · {model}
    </span>
  </div>
)
